import React, { useState, useEffect, useCallback, useMemo } from "react";
import { request } from "../../utils/api";
import { getSessionTenantId } from "../../utils/tenantContext";
import { toast } from "react-hot-toast";
import {
  UserPlusIcon,
  SearchIcon,
  CloseIcon,
  ChevronDownIcon,
} from "../../Icons";
import Table from "../../components/UI/Table";
import {
  PAGE_SHELL,
  PAGE_HEADER_ROW,
  PAGE_TITLE,
  PAGE_SUBTITLE,
  PANEL,
  PANEL_TOOLBAR,
  CELL_MUTED,
  CELL_STRONG,
  SPINNER,
} from "../../ui/pageChrome";
import {
  SURVEYOR_BTN,
  SURVEYOR_FIELD,
  SURVEYOR_MODAL,
  SURVEYOR_ROLE_CHIP,
  SURVEYOR_THEME,
} from "./surveyorTheme";

const STATUS_FILTERS = [
  { value: "", label: "All Status" },
  { value: "Active", label: "Active" },
  { value: "Inactive", label: "Inactive" },
];

const SurveyorAssignment = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [surveyors, setSurveyors] = useState([]);
  const [selectedSurveyorIds, setSelectedSurveyorIds] = useState([]);
  const [loadingSurveyors, setLoadingSurveyors] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const fetchEmployees = useCallback(
    async (pageNo = 1) => {
      setLoading(true);
      try {
        const tenantId = getSessionTenantId();
        if (!tenantId) {
          toast.error("Tenant context is missing. Please sign in again.");
          return;
        }

        let url = `/surveyor/employee-list?page=${pageNo}&limit=20&tenantId=${tenantId}`;
        if (debouncedSearch) {
          url += `&search=${encodeURIComponent(debouncedSearch)}`;
        }

        const response = await request(url);

        if (response.status === 1) {
          setEmployees(response.result.employees);
          setTotalPages(response.result.totalPages);
          setPage(response.result.page);
          setTotalItems(
            response.result.total || response.result.employees?.length || 0,
          );
          setItemsPerPage(response.result.limit || 20);
        } else {
          toast.error(response.message || "Failed to fetch employees");
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Error loading employees");
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch],
  );

  const fetchSurveyors = useCallback(async () => {
    setLoadingSurveyors(true);
    try {
      const tenantId = getSessionTenantId();
      if (!tenantId) {
        toast.error("Tenant context is missing. Please sign in again.");
        return;
      }
      const response = await request(
        `/surveyor/surveyor-list?page=1&limit=100&tenantId=${tenantId}`,
      );
      if (response.status === 1) {
        setSurveyors(response.result.surveyors);
      } else {
        setSurveyors([]);
      }
    } catch (error) {
      console.error("Error fetching surveyors:", error);
      toast.error("Error loading surveyors list");
    } finally {
      setLoadingSurveyors(false);
    }
  }, []);

  useEffect(() => {
    fetchEmployees(page);
  }, [page, fetchEmployees, debouncedSearch]);

  const roleOptions = useMemo(() => {
    const roles = new Set();
    (employees || []).forEach((emp) => {
      if (emp.role) roles.add(String(emp.role));
    });
    return [
      { value: "", label: "All Roles" },
      ...Array.from(roles)
        .sort((a, b) => a.localeCompare(b))
        .map((role) => ({ value: role, label: role })),
    ];
  }, [employees]);

  const hasActiveFilters = Boolean(
    searchTerm.trim() || roleFilter || statusFilter,
  );

  const clearFilters = () => {
    setSearchTerm("");
    setRoleFilter("");
    setStatusFilter("");
  };

  const filteredEmployees = useMemo(() => {
    return (employees || []).filter((emp) => {
      if (roleFilter && String(emp.role || "") !== roleFilter) return false;
      if (statusFilter && String(emp.status || "") !== statusFilter) return false;
      return true;
    });
  }, [employees, roleFilter, statusFilter]);

  const handleAssignClick = useCallback((employee) => {
    setSelectedEmployee(employee);
    const existingIds = employee.surveyors
      ? employee.surveyors.map((s) => s.id)
      : [];
    setSelectedSurveyorIds(existingIds);
    setIsModalOpen(true);
    fetchSurveyors();
  }, [fetchSurveyors]);

  const handleSurveyorToggle = (id) => {
    setSelectedSurveyorIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id],
    );
  };

  const handleSaveAssignment = async () => {
    if (!selectedEmployee) return;

    try {
      const tenantId = getSessionTenantId();
      if (!tenantId) {
        toast.error("Tenant context is missing. Please sign in again.");
        return;
      }

      const payload = {
        tenantId,
        employeeId: selectedEmployee.id,
        surveyorIds: selectedSurveyorIds,
      };

      const response = await request("/surveyor/assign", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (response.status === 1) {
        toast.success(response.message || "Surveyors assigned successfully");
        setIsModalOpen(false);
        fetchEmployees(page);
      } else {
        toast.error(
          response.message || response.error || "Error assigning surveyors",
        );
      }
    } catch (error) {
      console.error("Error assigning surveyors:", error);
      toast.error("Failed to assign surveyors");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Name",
        accessor: "firstName",
        className: "whitespace-nowrap px-3",
        render: (emp) => (
          <span className={CELL_STRONG}>
            {[emp.firstName, emp.lastName].filter(Boolean).join(" ") || "—"}
          </span>
        ),
      },
      {
        header: "Email",
        accessor: "email",
        className: "whitespace-nowrap px-3",
        render: (emp) => (
          <span className={CELL_MUTED}>{emp.email || "—"}</span>
        ),
      },
      {
        header: "Role",
        accessor: "role",
        className: "whitespace-nowrap px-3",
        render: (emp) => (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md text-[10px] font-semibold uppercase tracking-wide border ${SURVEYOR_ROLE_CHIP}`}
          >
            {emp.role || "—"}
          </span>
        ),
      },
      {
        header: "Status",
        accessor: "status",
        className: "whitespace-nowrap px-3",
        render: (emp) => (
          <span
            className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold ${
              emp.status === "Active"
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-slate-50 text-slate-600 border-slate-200"
            }`}
          >
            {emp.status || "—"}
          </span>
        ),
      },
      {
        header: "Assigned Surveyors",
        accessor: "surveyors",
        className: "px-3",
        render: (emp) =>
          emp.surveyors && emp.surveyors.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {emp.surveyors.map((s, idx) => (
                <span
                  key={s.id || idx}
                  className={`inline-flex items-center h-5 px-1.5 rounded-md border text-[10px] font-semibold ${SURVEYOR_ROLE_CHIP}`}
                >
                  {s.name}
                </span>
              ))}
            </div>
          ) : (
            <span className={`${CELL_MUTED} italic`}>None</span>
          ),
      },
      {
        header: "Actions",
        accessor: "actions",
        className: "whitespace-nowrap pl-3 pr-4",
        render: (emp) => (
          <button
            type="button"
            onClick={() => handleAssignClick(emp)}
            className={SURVEYOR_BTN.row}
            title="Assign surveyor"
            aria-label={`Assign surveyor to ${emp.firstName || "employee"}`}
          >
            <UserPlusIcon size={13} />
            Assign
          </button>
        ),
      },
    ],
    [handleAssignClick],
  );

  return (
    <div className={PAGE_SHELL}>
      <div className={PAGE_HEADER_ROW}>
        <div>
          <h1 className={PAGE_TITLE}>Surveyor Assignment</h1>
          <p className={PAGE_SUBTITLE}>Assign surveyors to employees</p>
        </div>
      </div>

      <div className={PANEL}>
        <div className={PANEL_TOOLBAR}>
          <div className="relative flex-1 min-w-0">
            <SearchIcon
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by name, email, role..."
              className={SURVEYOR_FIELD.search}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm ? (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600"
                aria-label="Clear search"
              >
                <CloseIcon size={14} />
              </button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="relative">
              <label className="sr-only" htmlFor="surveyor-role-filter">
                Filter by role
              </label>
              <select
                id="surveyor-role-filter"
                  className={`${SURVEYOR_FIELD.select} min-w-[130px] ${
                    roleFilter ? SURVEYOR_FIELD.selectActive : ""
                  }`}
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                {roleOptions.map((option) => (
                  <option key={option.value || "all-roles"} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            <div className="relative">
              <label className="sr-only" htmlFor="surveyor-status-filter">
                Filter by status
              </label>
              <select
                id="surveyor-status-filter"
                  className={`${SURVEYOR_FIELD.select} min-w-[130px] ${
                    statusFilter ? SURVEYOR_FIELD.selectActive : ""
                  }`}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_FILTERS.map((option) => (
                  <option
                    key={option.value || "all-status"}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon
                size={14}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
            </div>

            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className={SURVEYOR_BTN.clear}
              >
                Clear
              </button>
            ) : null}

            <span
              className={`h-8 inline-flex items-center px-2.5 rounded text-[11px] font-semibold tabular-nums ${SURVEYOR_THEME.section.badge}`}
            >
              {filteredEmployees.length}
              {hasActiveFilters ? ` / ${totalItems}` : ""} employees
            </span>
          </div>
        </div>

        <Table
          columns={columns}
          data={filteredEmployees}
          loading={loading}
          showSrNo
          density="compact"
          embedded
          headTone={SURVEYOR_THEME.tableHead}
          pagination={{
            currentPage: page,
            totalPages: totalPages,
            onPageChange: setPage,
            totalItems: totalItems,
            itemsPerPage: itemsPerPage,
          }}
          noDataMessage="No employees found."
        />
      </div>

      {isModalOpen ? (
        <div className={SURVEYOR_MODAL.backdrop}>
          <div className={`${SURVEYOR_MODAL.panel} max-w-lg`}>
            <div className={SURVEYOR_MODAL.head}>
              <div>
                <h3 className={SURVEYOR_MODAL.title}>Assign Surveyor</h3>
                <p className={SURVEYOR_MODAL.subtitle}>
                  {[selectedEmployee?.firstName, selectedEmployee?.lastName]
                    .filter(Boolean)
                    .join(" ") || "Employee"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className={SURVEYOR_BTN.modalClose}
                title="Close"
              >
                <CloseIcon size={16} />
              </button>
            </div>
            <div className={SURVEYOR_MODAL.body}>
              <div className="max-h-60 overflow-y-auto">
                {loadingSurveyors ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <span className={SPINNER} />
                    Loading surveyors...
                  </div>
                ) : surveyors.length === 0 ? (
                  <p className="text-sm text-slate-500">No surveyors available.</p>
                ) : (
                  <div className="space-y-2">
                    {surveyors.map((surveyor) => (
                      <label
                        key={surveyor.id}
                        htmlFor={`surveyor-${surveyor.id}`}
                        className="flex items-center gap-3 rounded-md border border-indigo-200 px-3 py-2 hover:bg-indigo-50/60 cursor-pointer"
                      >
                        <input
                          id={`surveyor-${surveyor.id}`}
                          type="checkbox"
                          className="h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-200"
                          checked={selectedSurveyorIds.includes(surveyor.id)}
                          onChange={() => handleSurveyorToggle(surveyor.id)}
                        />
                        <span className="min-w-0">
                          <span className={`${CELL_STRONG} block`}>
                            {[surveyor.firstName, surveyor.lastName]
                              .filter(Boolean)
                              .join(" ")}
                          </span>
                          <span className={`${CELL_MUTED} block truncate`}>
                            {surveyor.email}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className={SURVEYOR_MODAL.footer}>
                <button
                  type="button"
                  className={SURVEYOR_BTN.modalCancel}
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={SURVEYOR_BTN.modalPrimary}
                  onClick={handleSaveAssignment}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default SurveyorAssignment;
